import React, { useState } from "react";
import { useForm } from "react-hook-form";

import { InputAdornment, Checkbox, FormControlLabel } from "@material-ui/core";
import { bright_90, increaseBrightness } from "common/helpers/adjustColor";
import ValidationHelper from "common/helpers/validation.helper";
import upsdkService from "common/services/upsdkService";
import upsdkSelector from "common/store/selectors/upsdkSelector";
import configSelector from "common/store/selectors/configSelector";

import PhoneIcon from "../../../assets/icons/phone.icon";
import ProfileIcon from "../../../assets/icons/profile.icon";
import AppButton from "../../../common/button/index.component";
import InputField from "../../../common/fields/input-field/index.component";
import ServerError from "../../../common/server-error/index.component";
import Typography from "../../../common/typography/index.component";
import MailIcon from "../../../assets/icons/mail.icon";
import UncheckedCheckboxIcon from "../../../assets/icons/unchecked-checkbox.icon";
import CheckedCheckboxIcon from "../../../assets/icons/checked-checkbox.icon";
import { useTranslation } from "react-i18next";
import getCustomPageLinksForAuth from "../../../helpers/getCustomPageLinks";
import { getFooterLinks } from "common/helpers/getFooterLinks";
import { COLOR_DARK_LIVER } from "../../../constants/colors.constants";
import CountryHelper from "../../../helpers/countryCode.helper";
import MiscHelper from "common/helpers/misc.helper";
import {
  FORMAT_TYPE_SVG,
  FORMAT_TYPE_UNICODE,
} from "../../../constants/enums.constants";
import useRouteHook from "../../../hooks/useRoute.hook";

import "./index.component.scss";

export default function GuestCheckout({
  upsdk,
  primaryColor,
  secondaryColor,
  primaryTextColor,
  tertiaryColor,
  guestCheckoutSuccessCallback,
  config,
}) {
  const [loading, setLoading] = useState(false);
  const [guestLoginError, setGuestLoginError] = useState(undefined);
  const [termAgreement, setTermAgreement] = useState(false);
  const { register, handleSubmit, errors, setError } = useForm({
    mode: "onBlur",
  });
  const { CustomLink } = useRouteHook();

  // variables
  const currency = upsdkSelector.getCurrency({ upsdk });
  const isd_code = upsdkSelector.getISDCode({ upsdk });
  const isGuestCheckoutEmailRequired =
    configSelector.getIsEmailRequiredGuestCheckout({ config });
  const { t } = useTranslation();
  const footerLinks = getFooterLinks(configSelector.getCustomPages({ config }));
  const customPageLinks = getCustomPageLinksForAuth(footerLinks);

  const onSubmit = (data) => {
    const { fullName, phoneNumber, email } = data;

    if (!isNaN(fullName)) {
      return setError("fullName", {
        type: "custom",
        message: t("common.validUserNameHelperText"),
      });
    }

    setLoading(true);
    upsdkService
      .createUserForGuestCheckout({
        name: fullName,
        phone: `${isd_code}${phoneNumber}`,
        email: email,
      })
      .then((data) => {
        setGuestLoginError(null);
        guestCheckoutSuccessCallback(data);
      })
      .catch((e) => {
        if (e && e.data && e.data.result === "error.phone_exists") {
          // user already exists. no issues
          setGuestLoginError(null);
          guestCheckoutSuccessCallback(e.data);
          return;
        }

        setGuestLoginError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChange = (event) => {
    setTermAgreement(event.target.checked);
    setGuestLoginError(undefined);
  };

  return (
    <div className="guest-checkout-form">
      <div className="dialog-content">
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputField
            type="text"
            errors={errors}
            name="fullName"
            refForm={register(ValidationHelper.userName(t))}
            label={
              <Typography
                variant="h4"
                weight="bold"
                fontColor={primaryTextColor}
                isRequired={true}
              >
                {t("authDialog.fullName")}
              </Typography>
            }
            helperText={errors.fullName && errors.fullName.message}
            inputIcon={<ProfileIcon className="field-icon" />}
            primaryTextColor={primaryTextColor}
            iconBackgroundColor={increaseBrightness(secondaryColor, bright_90)}
          />
          <InputField
            type="number"
            refForm={register(ValidationHelper.phoneNumber(t, isd_code))}
            name="phoneNumber"
            errors={errors}
            label={
              <Typography
                variant="h4"
                weight="bold"
                fontColor={primaryTextColor}
                isRequired={true}
              >
                {t("authDialog.phoneNumber")}
              </Typography>
            }
            inputIcon={<PhoneIcon className="field-icon" />}
            primaryTextColor={primaryTextColor}
            iconBackgroundColor={increaseBrightness(primaryColor, bright_90)}
            helperText={errors.phoneNumber && errors.phoneNumber.message}
            inputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {MiscHelper.isWindowsOS() ? (
                    <img
                      height="16px"
                      width="16px"
                      src={CountryHelper.getCountryFlag(
                        isd_code,
                        currency,
                        FORMAT_TYPE_SVG,
                      )}
                      alt="country-flag"
                    />
                  ) : (
                    CountryHelper.getCountryFlag(
                      isd_code,
                      currency,
                      FORMAT_TYPE_UNICODE,
                    )
                  )}
                  &nbsp;
                  {isd_code}
                </InputAdornment>
              ),
            }}
          />
          {isGuestCheckoutEmailRequired && (
            <InputField
              type="email"
              name="email"
              errors={errors}
              className="full-width"
              refForm={register(ValidationHelper.email(t))}
              label={
                <Typography
                  variant="h4"
                  weight="bold"
                  fontColor={primaryTextColor}
                >
                  {t("authDialog.emailAddress")}
                </Typography>
              }
              helperText="Please provide valid email address"
              inputIcon={<MailIcon fill="#000000" className="field-icon" />}
              primaryTextColor={primaryTextColor}
              iconBackgroundColor={increaseBrightness(tertiaryColor, bright_90)}
            />
          )}
        </form>
      </div>
      {guestLoginError && <ServerError message={guestLoginError} />}
      <div className="dialog-footer">
        <div className="terms-check-wrapper">
          <FormControlLabel
            classes={{ root: "terms-check" }}
            control={
              <Checkbox
                icon={<UncheckedCheckboxIcon className="uncheck-icon" />}
                checkedIcon={<CheckedCheckboxIcon className="check-icon" />}
                onChange={handleChange}
                name="terms-check"
              />
            }
            label={
              <Typography
                variant="h3"
                weight="regular"
                fontColor={COLOR_DARK_LIVER}
                className="terms-check-label"
              >
                {t("authDialog.iAgreeTo")}
                <CustomLink
                  to={
                    customPageLinks.terms ||
                    "https://www.urbanpiper.com/legal/customer-terms-and-conditions"
                  }
                  target="_blank"
                >
                  <Typography
                    variant="h3"
                    weight="regular"
                    fontColor={primaryColor}
                  >
                    {t("authDialog.terms")}
                  </Typography>
                </CustomLink>{" "}
                {t("authDialog.and")}
                <CustomLink
                  to={
                    customPageLinks.policy ||
                    "https://www.urbanpiper.com/legal/privacy-policy"
                  }
                  target="_blank"
                >
                  <Typography
                    variant="h3"
                    weight="regular"
                    fontColor={primaryColor}
                  >
                    {t("authDialog.privacy&Policy")}
                  </Typography>
                </CustomLink>
              </Typography>
            }
          />
        </div>

        <AppButton
          fullWidth
          variant="contained"
          className="guest-checkout-button"
          isLoading={loading}
          buttonColor={primaryColor}
          onClickCallback={handleSubmit(onSubmit)}
          isDisabled={!termAgreement}
        >
          <Typography
            variant="h2"
            weight="bold"
            className="guest-checkout-button-text"
          >
            {t("authDialog.continueAsGuest")}
          </Typography>
        </AppButton>
      </div>
    </div>
  );
}
